<template>
  <WdsModal :show="isOpen" :title="$t('Request payment')" @cancel="emit('cancel')">
    <WdsSelect
      v-model="selectedJob"
      :disabled="loadingHires || !hires.length"
      :label="$t('Which hire is this request related to?')"
      :placeholder="placeholderText"
      :list="hireOptions"
      data-test="payment-requests-modal-contract-select"
    >
    <template #option="{ option }">
      <WdsText :sub-text="option.subLabel" class="tw-max-w-full">
        <WdsStack gap-sm align-center>
          <div class="tw-truncate">{{ option.label }}</div>
          <HireActiveStatusLabel :status="option.status" xs />
        </WdsStack>
      </WdsText>
    </template>
    <template #header="{ option }">
      <WdsStack v-if="option" gap-sm align-center class="tw-w-full">
        <WdsText class="tw-truncate">{{ option.label }}</WdsText>
        <HireActiveStatusLabel :status="option.status" xs />
      </WdsStack>
    </template>
    </WdsSelect>
    <template #footer>
      <div class="tw-flex tw-justify-between">
        <WdsButton data-test="payment-requests-modal-cancel-button" @click="emit('cancel')">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton v-if="!url" data-test="payment-requests-modal-continue-button" disabled primary>
          {{ $t('Continue') }}
        </WdsButton>
        <WdsButton
          v-if="url"
          data-test="payment-requests-modal-continue-button"
          primary
          :href="url"
        >
          {{ $t('Continue') }}
        </WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import moment from 'moment'
import { WdsButton, WdsModal, WdsSelect, WdsText, WdsStack } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { useTranslation } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { HireActiveStatus } from '@/gql/graphql'
import HireActiveStatusLabel from '@/components/Common/HireActiveStatusLabel.vue'

interface Props {
  isOpen: boolean
  showWorkerName: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  showWorkerName: false,
})

const emit = defineEmits(['cancel'])

const { $t } = useTranslation()

const { result: activeHires, loading: loadingHires } = useQuery(
  gql`
    query hires($accounts: [ID!]) {
      hires(first: 100000, accounts: $accounts, eligibleForPaymentRequests: true, orderBy: [{ field: START_DATE, order: DESC }]) {
        data {
          id
          status
          company {
            name
          }
          startDate
          endDate
          job {
            id
            name
          }
          worker {
            name
          }
        }
      }
    }
  `,
  {
    accounts: [currentAccount.globalId],
  },
  {
    clientId: 'publicApi',
  },
)

const hires = computed(() => activeHires.value?.hires?.data ?? [])

interface HireOption {
  value: string
  label: string
  subLabel: string
  status: HireActiveStatus
}

const hireOptions = computed<HireOption[]>(() =>
  hires.value.map((hire) => ({
    value: hire.id,
    label: props.showWorkerName ? `${hire.job.name} (${hire.worker.name})` : hire.job.name,
    subLabel: $t(':company, :startDate - :endDate', {
      company: hire.company.name,
      startDate: hire.startDate ? moment(hire.startDate).format('MMM D YYYY') : $t('No start date'),
      endDate: hire.endDate ? moment(hire.endDate).format('MMM D YYYY') : $t('No end date'),
    }),
    status: hire.status,
  })),
)

const selectedJob = ref<HireOption | null>(null)

const placeholderText = computed(() => {
  if (loadingHires.value) return $t('Loading...')
  if (!hires.value.length) return $t('No active contracts available')
  return $t('Select hire')
})

const url = computed(() => {
  if (!selectedJob.value) return null

  return laravelNamedRoute(namedRoutes.bills.create, { hire: selectedJob.value.value })
})
</script>

